import type { DateRangeValue } from "@/components/ui/date-range-picker";
import { lastMonthsRange } from "@/lib/date-utils";
import { VM_PLAN_TYPES, type VmPlanType } from "@/types/admin";
import { createTableFiltersStore } from "./createTableFiltersStore";

export interface ResourceReinstallListFiltersState extends Record<
  string,
  unknown
> {
  /** VM name, VM UUID, IP address, an id, or the requester's e-mail or phone. Empty means no term. */
  search: string;

  /** "All" means both trial and paid VMs. */
  vmPlan: VmPlanType | "All";

  /** Requested-at range, ISO strings. Both ends or neither — the API ignores a half range. */
  dateRange: DateRangeValue;
}

// Opens on the last six months, like the other cross-tenant lists: the table grows a row per
// reinstall, and the VM-name search reads only the rows the range leaves.
const defaults: ResourceReinstallListFiltersState = {
  search: "",
  vmPlan: "All",
  dateRange: lastMonthsRange(),
};

/**
 * Filter and paging state for the admin VM-reinstall report, cached in sessionStorage.
 *
 * Bump `version` in step with any change to the shape above — a stored filter the backend no
 * longer accepts comes back as a 400 on something the operator cannot see.
 */
export const useResourceReinstallFiltersStore =
  createTableFiltersStore<ResourceReinstallListFiltersState>({
    name: "admin.resourceReinstalls.filters",
    // v2: the tenant filter was removed (a tenant id is pasted into the search box instead). Bumped
    // so a stored tenantId cannot go on filtering the list with nothing on screen to clear it.
    version: 2,
    defaults,
    defaultPageSize: 20,
    sanitize: (filters) => ({
      ...filters,
      vmPlan: VM_PLAN_TYPES.some((plan) => plan === filters.vmPlan)
        ? filters.vmPlan
        : "All",

      // Exactly one end is not a range. An empty range is left alone: it is the operator having
      // cleared the six-month default to search all time.
      dateRange:
        Boolean(filters.dateRange?.from) !== Boolean(filters.dateRange?.to)
          ? defaults.dateRange
          : filters.dateRange,
    }),
  });
